import { useState, useEffect } from "react";
import { 
  FaWallet, FaMoneyBillWave, FaUser, FaCalendar, FaSpinner, 
  FaChevronLeft, FaChevronRight, FaSearch, FaDownload, 
  FaCheckCircle, FaTimesCircle, FaClock, FaEye, FaEdit, 
  FaTrash, FaTimes, FaCheck, FaCopy, FaPhone, FaEnvelope,
  FaArrowUp, FaArrowDown, FaFilter, FaSort, FaHistory,
  FaHourglassHalf, FaBan, FaExclamationTriangle
} from "react-icons/fa";
import { utils, writeFile } from "xlsx";
import axios from "axios";
import { toast } from "react-hot-toast";

const RedemptionRequests = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  
  // Modal States
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Edit Form State
  const [editStatus, setEditStatus] = useState("");
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Filter States
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  
  const requestsPerPage = 10;

  useEffect(() => {
    fetchRequests();
  }, []);

  // Fetch all redemption requests
  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get("https://api.dreamtoday.in/api/admin/allredemptionreq", {
        withCredentials: true
      });
      
      console.log("API Response:", response.data);
      
      if (response.data && response.data.success) {
        setRequests(Array.isArray(response.data.requests) ? response.data.requests : []);
        setFilteredRequests(Array.isArray(response.data.requests) ? response.data.requests : []);
      } else {
        setRequests([]);
        setFilteredRequests([]);
        setError(response.data?.message || "Failed to fetch redemption requests");
      }
    } catch (error) {
      console.error("Error fetching redemption requests:", error);
      setRequests([]);
      setFilteredRequests([]);
      setError(error.response?.data?.message || "Failed to fetch redemption requests");
    } finally {
      setLoading(false);
    }
  };

  // Update request status
  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    
    if (!editStatus) {
      toast.error("Please select a status");
      return;
    }

    try {
      setUpdating(true);
      
      const response = await axios.put(
        `https://api.dreamtoday.in/api/admin/updateredemptionreq/${selectedRequest._id}`,
        { status: editStatus },
        { withCredentials: true }
      );

      if (response.data && response.data.success) {
        toast.success("Request status updated successfully!");
        
        // Update local state
        const updatedRequests = requests.map(req => 
          req._id === selectedRequest._id 
            ? { ...req, status: editStatus, updatedAt: new Date().toISOString() }
            : req
        );
        
        setRequests(updatedRequests);
        
        // Close modal and reset
        setIsEditModalOpen(false);
        setSelectedRequest(null);
        setEditStatus("");
      } else {
        toast.error(response.data?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.message || "Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  // Delete request
  const handleDeleteRequest = async () => {
    try {
      setDeleting(true);
      
      const response = await axios.delete(
        `https://api.dreamtoday.in/api/admin/deleteredemptionreq/${selectedRequest._id}`,
        { withCredentials: true }
      );

      if (response.data && response.data.success) {
        toast.success("Request deleted successfully!");
        
        // Update local state
        const updatedRequests = requests.filter(req => req._id !== selectedRequest._id);
        setRequests(updatedRequests);
        
        // Close modal and reset
        setIsDeleteModalOpen(false);
        setSelectedRequest(null);
      } else {
        toast.error(response.data?.message || "Failed to delete request");
      }
    } catch (error) {
      console.error("Error deleting request:", error);
      toast.error(error.response?.data?.message || "Failed to delete request");
    } finally {
      setDeleting(false);
    }
  };

  // Search and filter
  useEffect(() => {
    let filtered = [...requests];
    
    // Apply search
    if (search) {
      const searchTerm = search.toLowerCase();
      filtered = filtered.filter(req => 
        req.userId?.name?.toLowerCase().includes(searchTerm) ||
        req.userId?.email?.toLowerCase().includes(searchTerm) ||
        req.userId?.phone?.toLowerCase().includes(searchTerm) ||
        req.upiId?.toLowerCase().includes(searchTerm) ||
        req._id?.toLowerCase().includes(searchTerm) ||
        req.amount?.toString().includes(searchTerm)
      );
    }
    
    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(req => req.status === statusFilter);
    }
    
    // Apply date filter
    if (dateFilter !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const thisWeek = new Date(today);
      thisWeek.setDate(thisWeek.getDate() - 7);
      const thisMonth = new Date(today);
      thisMonth.setMonth(thisMonth.getMonth() - 1);
      
      filtered = filtered.filter(req => {
        const reqDate = new Date(req.createdAt);
        switch(dateFilter) {
          case "today":
            return reqDate >= today;
          case "yesterday":
            return reqDate >= yesterday && reqDate < today;
          case "week":
            return reqDate >= thisWeek;
          case "month":
            return reqDate >= thisMonth;
          default:
            return true;
        }
      });
    }
    
    setFilteredRequests(filtered);
    setCurrentPage(1);
  }, [search, statusFilter, dateFilter, requests]);

  // Export data
  const exportData = (type) => {
    const exportData = filteredRequests.map((req) => ({
      ID: req._id || "N/A",
      UserName: req.userId?.name || "N/A",
      Email: req.userId?.email || "N/A",
      Phone: req.userId?.phone || "N/A",
      Amount: req.amount || 0,
      UPI_ID: req.upiId || "N/A",
      Status: req.status || "N/A",
      CreatedAt: req.createdAt ? new Date(req.createdAt).toLocaleString() : "N/A",
      UpdatedAt: req.updatedAt ? new Date(req.updatedAt).toLocaleString() : "N/A"
    }));
    
    const ws = utils.json_to_sheet(exportData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "RedemptionRequests");
    writeFile(wb, `redemption_requests_${new Date().toISOString().split('T')[0]}.${type}`);
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    if (text) {
      navigator.clipboard.writeText(text);
      setCopiedId(text);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch(status?.toLowerCase()) {
      case "completed":
        return (
          <span className="inline-flex items-center px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <FaCheckCircle className="mr-1" size={12} />
            Completed
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <FaClock className="mr-1" size={12} />
            Pending
          </span>
        );
      case "cancelled":
      case "rejected":
        return (
          <span className="inline-flex items-center px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <FaTimesCircle className="mr-1" size={12} />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            <FaHourglassHalf className="mr-1" size={12} />
            {status || "Unknown"}
          </span>
        );
    }
  };

  // Get status icon for table
  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case "completed":
        return <FaCheckCircle className="text-green-500" size={14} />;
      case "pending":
        return <FaClock className="text-yellow-500" size={14} />;
      case "cancelled":
      case "rejected":
        return <FaTimesCircle className="text-red-500" size={14} />;
      default:
        return <FaHourglassHalf className="text-gray-500" size={14} />;
    }
  };

  // Calculate statistics
  const calculateStats = () => {
    const totalRequests = requests.length;
    const totalAmount = requests.reduce((sum, req) => sum + (req.amount || 0), 0);
    const pendingRequests = requests.filter(req => req.status?.toLowerCase() === "pending").length;
    const completedRequests = requests.filter(req => req.status?.toLowerCase() === "completed").length;
    const pendingAmount = requests
      .filter(req => req.status?.toLowerCase() === "pending")
      .reduce((sum, req) => sum + (req.amount || 0), 0);
    const completedAmount = requests
      .filter(req => req.status?.toLowerCase() === "completed")
      .reduce((sum, req) => sum + (req.amount || 0), 0);
    
    return { totalRequests, totalAmount, pendingRequests, completedRequests, pendingAmount, completedAmount };
  };

  const stats = calculateStats();

  // Pagination
  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = filteredRequests.slice(indexOfFirstRequest, indexOfLastRequest);
  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading redemption requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <FaWallet className="text-blue-600" />
          Wallet Withdrawal Requests
        </h1>
        <p className="text-gray-600">Manage user withdrawal requests and update status</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Requests</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalRequests}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FaWallet className="text-blue-600 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Amount</p>
              <p className="text-2xl font-bold text-green-600">₹{stats.totalAmount.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <FaMoneyBillWave className="text-green-600 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pendingRequests}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <FaClock className="text-yellow-600 text-xl" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">Amount: ₹{stats.pendingAmount.toLocaleString()}</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats.completedRequests}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <FaCheckCircle className="text-green-600 text-xl" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">Amount: ₹{stats.completedAmount.toLocaleString()}</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg. Amount</p>
              <p className="text-2xl font-bold text-purple-600">
                ₹{stats.totalRequests > 0 ? Math.round(stats.totalAmount / stats.totalRequests) : 0}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <FaMoneyBillWave className="text-purple-600 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.totalRequests > 0 ? Math.round((stats.completedRequests / stats.totalRequests) * 100) : 0}%
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FaHistory className="text-blue-600 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2 relative">
            <FaSearch className="absolute left-4 top-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by user name, email, phone, UPI ID or amount..."
              className="w-full px-4 py-3 pl-12 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <FaFilter className="absolute left-4 top-3.5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-3 pl-12 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none appearance-none"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Date Filter */}
          <div className="relative">
            <FaCalendar className="absolute left-4 top-3.5 text-gray-400" />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-4 py-3 pl-12 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none appearance-none"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>

        {/* Export Buttons */}
        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={() => exportData("csv")}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2 text-sm font-medium"
            disabled={filteredRequests.length === 0}
          >
            <FaDownload />
            Export CSV
          </button>
          <button
            onClick={() => exportData("xlsx")}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-2 text-sm font-medium"
            disabled={filteredRequests.length === 0}
          >
            <FaDownload />
            Export Excel
          </button>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  S.No
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  User Details
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Amount & UPI
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Request Date
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentRequests.length > 0 ? (
                currentRequests.map((request, index) => (
                  <tr key={request._id} className="hover:bg-blue-50/30 transition-colors">
                    <td className="py-4 px-6 text-sm text-gray-600">
                      {indexOfFirstRequest + index + 1}
                    </td>
                    
                    <td className="py-4 px-6">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                          <FaUser className="text-white" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-gray-900">
                            {request.userId?.name || "N/A"}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <FaEnvelope size={10} />
                            <span>{request.userId?.email || "N/A"}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <FaPhone size={10} />
                            <span>{request.userId?.phone || "N/A"}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="py-4 px-6">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <FaMoneyBillWave className="text-green-500" size={14} />
                          <span className="text-lg font-bold text-green-600">₹{request.amount}</span>
                        </div>
                        {request.upiId && (
                          <div className="flex items-center gap-2">
                            <FaWallet className="text-purple-500" size={12} />
                            <span className="text-xs font-mono bg-purple-50 text-purple-700 px-2 py-1 rounded">
                              {request.upiId}
                            </span>
                            <button
                              onClick={() => copyToClipboard(request.upiId)}
                              className="text-gray-400 hover:text-purple-600"
                            >
                              {copiedId === request.upiId ? <FaCheck size={10} className="text-green-500" /> : <FaCopy size={10} />}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(request.status)}
                        {getStatusBadge(request.status)}
                      </div>
                    </td>
                    
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FaCalendar className="text-gray-400" size={12} />
                        <span>{formatDate(request.createdAt)}</span>
                      </div>
                    </td>
                    
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FaCalendar className="text-gray-400" size={12} />
                        <span>{formatDate(request.updatedAt)}</span>
                      </div>
                    </td>
                    
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setIsViewModalOpen(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition border border-blue-200 hover:border-blue-300"
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        
                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setEditStatus(request.status);
                            setIsEditModalOpen(true);
                          }}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition border border-green-200 hover:border-green-300"
                          title="Update Status"
                        >
                          <FaEdit />
                        </button>
                        
                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setIsDeleteModalOpen(true);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition border border-red-200 hover:border-red-300"
                          title="Delete Request"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="py-16 text-center">
                    <div className="text-gray-300 mb-4">
                      <FaWallet className="h-20 w-20 mx-auto" />
                    </div>
                    <p className="text-gray-500 text-xl font-medium mb-2">No requests found</p>
                    <p className="text-gray-400">
                      {search || statusFilter !== "all" || dateFilter !== "all" 
                        ? "Try adjusting your filters" 
                        : "No withdrawal requests yet"}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {filteredRequests.length > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white rounded-xl shadow-lg p-4">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold text-blue-600">{indexOfFirstRequest + 1}</span> to{" "}
            <span className="font-semibold text-blue-600">{Math.min(indexOfLastRequest, filteredRequests.length)}</span> of{" "}
            <span className="font-semibold text-blue-600">{filteredRequests.length}</span> requests
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            >
              <FaChevronLeft size={12} /> Previous
            </button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`w-10 h-10 rounded-lg transition font-medium ${
                      currentPage === pageNumber
                        ? "bg-blue-600 text-white shadow-md"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            >
              Next <FaChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {isViewModalOpen && selectedRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={() => {
            setIsViewModalOpen(false);
            setSelectedRequest(null);
          }}></div>
          
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <FaWallet className="text-2xl" />
                    <div>
                      <h2 className="text-2xl font-bold">Withdrawal Request Details</h2>
                      <p className="text-blue-100">Request ID: {selectedRequest._id.slice(-8)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsViewModalOpen(false);
                      setSelectedRequest(null);
                    }}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-6">
                  {/* User Information */}
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-5 border border-blue-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <div className="p-2 bg-blue-500 rounded-lg mr-3">
                        <FaUser className="text-white" />
                      </div>
                      User Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Name</p>
                        <p className="text-lg font-semibold text-gray-900">{selectedRequest.userId?.name}</p>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="text-gray-900">{selectedRequest.userId?.email}</p>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Phone</p>
                        <p className="text-gray-900">{selectedRequest.userId?.phone || "N/A"}</p>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">User ID</p>
                        <p className="text-sm font-mono text-gray-600">{selectedRequest.userId?._id}</p>
                      </div>
                    </div>
                  </div>

                  {/* Withdrawal Details */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <div className="p-2 bg-green-500 rounded-lg mr-3">
                        <FaMoneyBillWave className="text-white" />
                      </div>
                      Withdrawal Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Amount</p>
                        <p className="text-3xl font-bold text-green-600">₹{selectedRequest.amount}</p>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Status</p>
                        <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                      </div>
                      {selectedRequest.upiId && (
                        <div className="bg-white p-4 rounded-xl shadow-sm md:col-span-2">
                          <p className="text-sm text-gray-500">UPI ID</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-lg font-mono bg-purple-50 text-purple-700 px-3 py-1 rounded">
                              {selectedRequest.upiId}
                            </span>
                            <button
                              onClick={() => copyToClipboard(selectedRequest.upiId)}
                              className="p-2 text-gray-400 hover:text-purple-600"
                            >
                              <FaCopy />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <div className="p-2 bg-purple-500 rounded-lg mr-3">
                        <FaCalendar className="text-white" />
                      </div>
                      Timeline
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Requested On</p>
                        <p className="font-semibold">{formatDate(selectedRequest.createdAt)}</p>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Last Updated</p>
                        <p className="font-semibold">{formatDate(selectedRequest.updatedAt)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t p-6 rounded-b-2xl">
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setIsViewModalOpen(false);
                      setEditStatus(selectedRequest.status);
                      setIsEditModalOpen(true);
                    }}
                    className="flex-1 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition font-medium shadow-lg flex items-center justify-center gap-2"
                  >
                    <FaEdit />
                    Update Status
                  </button>
                  <button
                    onClick={() => {
                      setIsViewModalOpen(false);
                      setSelectedRequest(null);
                    }}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Status Modal */}
      {isEditModalOpen && selectedRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={() => {
            setIsEditModalOpen(false);
            setSelectedRequest(null);
            setEditStatus("");
          }}></div>
          
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <FaEdit className="text-2xl" />
                    <div>
                      <h2 className="text-2xl font-bold">Update Status</h2>
                      <p className="text-green-100">Change withdrawal request status</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedRequest(null);
                      setEditStatus("");
                    }}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
              </div>

              <form onSubmit={handleUpdateStatus} className="p-6">
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-2">User:</p>
                  <div className="bg-gray-50 p-3 rounded-lg mb-4">
                    <p className="font-semibold">{selectedRequest.userId?.name}</p>
                    <p className="text-sm text-gray-600">Amount: ₹{selectedRequest.amount}</p>
                  </div>

                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Update Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                    required
                  >
                    <option value="">Select status...</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    Current status: <span className="font-medium">{selectedRequest.status}</span>
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={updating}
                    className="flex-1 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {updating ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <FaCheck />
                        Update Status
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedRequest(null);
                      setEditStatus("");
                    }}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={() => {
            setIsDeleteModalOpen(false);
            setSelectedRequest(null);
          }}></div>
          
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="bg-gradient-to-r from-red-600 to-pink-600 text-white p-6 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <FaExclamationTriangle className="text-2xl" />
                    <h2 className="text-2xl font-bold">Delete Request</h2>
                  </div>
                  <button
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setSelectedRequest(null);
                    }}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="text-center mb-6">
                  <FaTrash className="text-5xl text-red-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Are you sure?</h3>
                  <p className="text-gray-600">
                    This action cannot be undone. This will permanently delete the withdrawal request
                    from <span className="font-semibold">{selectedRequest.userId?.name}</span> for 
                    <span className="font-semibold text-green-600"> ₹{selectedRequest.amount}</span>.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleDeleteRequest}
                    disabled={deleting}
                    className="flex-1 py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-xl hover:from-red-700 hover:to-pink-700 transition font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {deleting ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <FaTrash />
                        Delete Request
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setSelectedRequest(null);
                    }}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RedemptionRequests;